// Display helpers for Copilot previews. Every number shown here was computed
// by the backend (prices, effective prices, counts); nothing is recalculated.

export const pairText = (value, lang) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return (lang === "en" ? value.en || value.ar : value.ar || value.en) || "";
};

const number = (value, lang) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(lang === "ar" ? "ar-SA-u-nu-latn" : "en-US", { maximumFractionDigits: 2 }).format(n);
};

export const money = (value, lang) => (value === null || value === undefined ? "—" : `${number(value, lang)} ${lang === "ar" ? "ر.س" : "SAR"}`);

export const discountText = (discount, lang) => {
  if (!discount) return "";
  return discount.type === "percentage" ? `${number(discount.value, lang)}%` : money(discount.value, lang);
};

// { price, discount, effectivePrice } -> "20 SAR" or "20 SAR → 18 SAR (10%)"
export function priceText(state, lang) {
  if (!state) return "—";
  const base = money(state.price, lang);
  if (!state.discount) return base;
  return `${base} · ${discountText(state.discount, lang)} = ${money(state.effectivePrice, lang)}`;
}

// A preview row's before/after state as short text.
export function stateText(state, t, lang) {
  if (state === null || state === undefined) return "";
  if ("effectivePrice" in state || "price" in state) return priceText(state, lang);
  if ("section" in state) return pairText(state.section, lang);
  if ("description" in state) return pairText(state.description, lang) || "—";
  if ("name" in state) return pairText(state.name, lang);
  if ("calories" in state) return pairText(state.calories, lang) || "—";
  if ("allergens" in state) return pairText(state.allergens, lang) || "—";
  if ("linked" in state) return state.linked ? t.values.linked : t.values.notLinked;
  if ("assigned" in state) return state.assigned ? t.values.assigned : t.values.notAssigned;
  if ("image" in state) return state.image ? t.values.hasImage : t.values.noImage;
  if ("usedBy" in state) return t.values.usedBy(state.usedBy);
  if ("archived" in state) return t.values.deleted;
  return "";
}

// A preview field value as text.
export function fieldValue(field, t, lang) {
  const { key, value } = field;
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? t.values.yes : t.values.no;
  if (key === "discountValue") return field.discountType === "amount" ? money(value, lang) : `${number(value, lang)}%`;
  if (key === "price") return typeof value === "object" ? priceText(value, lang) : money(value, lang);
  if (key === "priceChangeValue") return number(value, lang);
  if (["discountType", "priceChangeMode", "contentField", "contentLanguage", "contentMode"].includes(key)) return t.values[value] || String(value);
  if (key === "pricingNames") return value.join(lang === "ar" ? "، " : ", ");
  if (key === "copiedAdditions") return value.map((name) => pairText(name, lang)).join(lang === "ar" ? "، " : ", ");
  if (key === "options") return value.map((o) => `${pairText(o.name, lang)} (${money(o.price, lang)})`).join(lang === "ar" ? "، " : ", ");
  if (key === "addition") return additionText(value, t, lang);
  if (key === "lifecycle") return t.lifecycle[value] || String(value);
  if (key === "order") return value.map((name, i) => `${i + 1}. ${pairText(name, lang)}`).join(lang === "ar" ? "، " : ", ");
  if (key === "image") return value ? t.values.hasImage : t.values.noImage;
  if (key === "source" || key === "destination") return [value.restaurant, value.menu, pairText(value.section, lang)].filter(Boolean).join(" › ");
  if (typeof value === "object") return pairText(value, lang);
  return String(value);
}

export function additionText(addition, t, lang) {
  if (!addition) return "—";
  const options = (addition.options || []).map((o) => `${pairText(o.name, lang)} ${money(o.price, lang)}`).join(lang === "ar" ? "، " : ", ");
  return `${pairText(addition.name, lang)} · ${t.fields.min} ${addition.min} / ${t.fields.max} ${addition.max}${addition.isMandatory ? ` · ${t.fields.isMandatory}` : ""} · ${options}`;
}

// Discount type is shown next to its value.
export function withContext(fields) {
  const type = fields.find((f) => f.key === "discountType")?.value;
  return fields.map((f) => (f.key === "discountValue" ? { ...f, discountType: type } : f));
}

export const PLAN_STATUS_VARIANT = {
  pending: "warning",
  executing: "info",
  completed: "success",
  partially_completed: "warning",
  failed: "danger",
  cancelled: "neutral",
  superseded: "neutral",
  expired: "neutral",
};

// Inputs offered by "Edit" for each editable param.
export const PARAM_INPUTS = {
  nameAr: { type: "text" },
  nameEn: { type: "text" },
  descriptionAr: { type: "textarea" },
  descriptionEn: { type: "textarea" },
  price: { type: "number" },
  calories: { type: "number" },
  priceChangeValue: { type: "number" },
  discountValue: { type: "number" },
  min: { type: "number" },
  max: { type: "number" },
  discountType: { type: "select", options: ["percentage", "amount"] },
  priceChangeMode: { type: "select", options: ["set", "increase_amount", "decrease_amount", "increase_percent", "decrease_percent"] },
  replaceExisting: { type: "checkbox" },
  isMandatory: { type: "checkbox" },
};
